import { useEffect } from "react";

/**
 * Environment indicator banner + title updater.
 *
 * - In prod: renders nothing, leaves the static <title>Booma Partner Portal</title>
 *   from index.html alone.
 *   (static title is "Booma Partner Portal")
 * - In any non-prod env (dev, staging, local — anything other than "PROD"):
 *   renders a slim sticky banner across the top of the viewport AND
 *   appends the env name to the document title (e.g. "Booma Partner Portal | DEV").
 *
 * Source of truth: REACT_APP_ENV_NAME env var.
 *   - prod build pipeline injects "PROD"
 *   - dev build pipeline injects "DEV"
 *   - local dev (unset) defaults to "DEV" — fail-safe; better to over-mark
 *     than under-mark a non-prod environment.
 */
export default function EnvBanner() {
  const envName = (process.env.REACT_APP_ENV_NAME || "DEV").toUpperCase();
  const isProd = envName === "PROD";

  useEffect(() => {
    const root = document.documentElement;
    if (isProd) {
      root.style.setProperty("--env-banner-h", "0px");
      return; // leave the static title from index.html as-is
    }
    root.style.setProperty("--env-banner-h", "1.75rem"); // must match the h-7 banner below
    const original = document.title;
    document.title = `${original} | ${envName}`;
    return () => {
      document.title = original;
      root.style.setProperty("--env-banner-h", "0px");
    };
  }, [isProd, envName]);

  if (isProd) return null;

  return (
    <div
      role="status"
      aria-label={`${envName} environment notice`}
      className="fixed inset-x-0 top-0 z-[1000] flex h-7 items-center justify-center bg-amber-500 px-3 text-center text-xs font-semibold uppercase tracking-wide text-amber-950 shadow-sm"
      style={{
        // Diagonal stripe overlay so this is unmistakable, not just a color
        backgroundImage:
          "repeating-linear-gradient(45deg, rgba(0,0,0,0.08) 0 8px, transparent 8px 16px)",
      }}
    >
      <span>
        {envName} ENVIRONMENT - internal use only. Data here is not production.
      </span>
    </div>
  );
}
